import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Truck, Package, Users, MapPin, Clock, Shield, Box, TruckIcon, Menu, X, Smartphone, UserCheck, QrCode, Bell, Globe, CreditCard, History, Zap } from 'lucide-react';
import { AppFooter } from '@/components/layout/AppFooter';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '@/components/LanguageSelector';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { useState } from 'react';
import logoFull from '@/assets/logo-full.svg';
import vectorBgGood from '@/assets/vector-bg-good.svg';
import backgroundDashedStep6 from '@/assets/background-dashed-step6.svg';
import backgroundDashedStep2 from '@/assets/background-dashed-step2.svg';
import backgroundDashedStep4 from '@/assets/background-dashed-step4.svg';
import backgroundDashedHero from '@/assets/background-dashed-hero.svg';
import heroImage from '@/assets/hero-image.png';
import step1Icon from '@/assets/step-1-icon.png';
import step3Icon from '@/assets/step-3-icon.png';
import step5Icon from '@/assets/step-5-icon.png';
const Landing = () => {
  const {
    t
  } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const step1 = useScrollAnimation(0.1, true);
  const step2 = useScrollAnimation(0.1, true);
  const step3 = useScrollAnimation();
  const step4 = useScrollAnimation();
  const step5 = useScrollAnimation();
  const step6 = useScrollAnimation();
  const features = [{
    icon: Package,
    title: t('landing.features.shipments.title'),
    description: t('landing.features.shipments.description')
  }, {
    icon: Truck,
    title: t('landing.features.fleet.title'),
    description: t('landing.features.fleet.description')
  }, {
    icon: MapPin,
    title: t('landing.features.tracking.title'),
    description: t('landing.features.tracking.description')
  }, {
    icon: Users,
    title: t('landing.features.multiUser.title'),
    description: t('landing.features.multiUser.description')
  }, {
    icon: Clock,
    title: t('landing.features.planning.title'),
    description: t('landing.features.planning.description')
  }, {
    icon: Shield,
    title: t('landing.features.security.title'),
    description: t('landing.features.security.description')
  }];
  return <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto flex h-20 items-center justify-between px-6">
          <img src={logoFull} alt="Velox" className="h-10" />
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <LanguageSelector />
            <Link to="/auth">
              <Button variant="ghost" className="text-[#37699E] hover:text-[#2E5782] font-semibold text-base">
                {t('auth.login.title')}
              </Button>
            </Link>
            <Link to="/auth?tab=signup">
              <Button className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-bold px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-all">
                {t('landing.hero.cta')}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-[#37699E] hover:bg-[#EBF4F8] rounded-lg transition-colors" aria-label="Toggle menu">
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && <div className="md:hidden bg-white border-t border-[#DBE2EA] shadow-lg">
            <div className="container mx-auto px-6 py-4 flex flex-col gap-4">
              <div className="flex justify-center pb-2 border-b border-[#DBE2EA]">
                <LanguageSelector />
              </div>
              <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="w-full text-[#37699E] hover:text-[#2E5782] font-semibold text-base">
                  {t('auth.login.title')}
                </Button>
              </Link>
              <Link to="/auth?tab=signup" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-bold px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-all">
                  {t('landing.hero.cta')}
                </Button>
              </Link>
            </div>
          </div>}
      </header>

      {/* Hero Section */}
      <section className="relative bg-[#FFF9F0] py-20 overflow-hidden" style={{
      backgroundImage: `url(${backgroundDashedHero})`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right center',
      backgroundSize: 'contain'
    }}>
        {/* Diagonal stripes background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-[#FFE4CC]/40 to-transparent transform rotate-12 translate-x-64 -translate-y-32"></div>
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-[#FFE4CC]/40 to-transparent transform -rotate-12 -translate-x-32 translate-y-32"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left content */}
            <div className="space-y-6">
              <h1 className="font-heading text-5xl font-semibold leading-tight text-[#37699E] lg:text-5xl">
                {t('landing.hero.title')}{' '}
                <span className="text-[#37699E]">—</span>{' '}
                <span className="text-[#37699E]">{t('landing.hero.titleHighlight')}</span>{' '}
                {t('landing.hero.titleEnd')}
              </h1>
              <p className="text-lg text-[#5A6B7A] leading-relaxed">
                {t('landing.hero.subtitle')}
              </p>
              <div className="pt-4">
                <Link to="/auth?tab=signup">
                  <Button size="lg" className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-bold px-8 py-4 text-lg rounded-lg shadow-md hover:shadow-lg transition-all">
                    {t('landing.hero.tryFree')}
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right illustration */}
            <div className="relative flex items-center justify-center">
              <div className="relative w-full max-w-2xl">
                <img src={heroImage} alt="Velox logistics illustration" className="w-full h-auto" loading="eager" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section - Transporters vs Shippers */}
      <section className="bg-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="text-center mb-16">
              <h2 className="font-semibold font-heading mb-4 text-4xl text-[#37699E]">
                {t('landing.comparison.title')}
              </h2>
              <p className="text-lg text-[#5A6B7A] max-w-2xl mx-auto">
                {t('landing.comparison.subtitle')}
              </p>
            </div>

            {/* Comparison Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Shipper Card */}
              <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-[#DBE2EA] hover:border-[#0880AE] transition-all duration-300">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-lg bg-[#37699E] flex items-center justify-center">
                    <Package className="text-white" size={24} />
                  </div>
                  <h3 className="text-2xl font-semibold text-[#37699E] font-heading">
                    {t('landing.comparison.shipper.title')}
                  </h3>
                </div>

                <ul className="space-y-4 mb-8">
                  {[0, 1, 2, 3, 4].map((index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-[#14A38B] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-[#2D3748] leading-relaxed">
                        {t(`landing.comparison.shipper.benefits.${index}`)}
                      </span>
                    </li>
                  ))}
                </ul>

                <Link to="/auth?tab=signup" className="block">
                  <Button className="w-full bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-bold py-3 rounded-lg shadow-md hover:shadow-lg transition-all">
                    {t('landing.comparison.shipper.cta')}
                  </Button>
                </Link>
              </div>

              {/* Transporter Card */}
              <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-[#DBE2EA] hover:border-[#0880AE] transition-all duration-300">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-lg bg-[#0880AE] flex items-center justify-center">
                    <Truck className="text-white" size={24} />
                  </div>
                  <h3 className="text-2xl font-semibold text-[#0880AE] font-heading">
                    {t('landing.comparison.transporter.title')}
                  </h3>
                </div>

                <ul className="space-y-4 mb-8">
                  {[0, 1, 2, 3, 4].map((index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-[#14A38B] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-[#2D3748] leading-relaxed">
                        {t(`landing.comparison.transporter.benefits.${index}`)}
                      </span>
                    </li>
                  ))}
                </ul>

                <Link to="/auth?tab=signup" className="block">
                  <Button className="w-full bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-bold py-3 rounded-lg shadow-md hover:shadow-lg transition-all">
                    {t('landing.comparison.transporter.cta')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      

      {/* Workflow Section */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="container mx-auto px-6">
          <div className="mb-16 text-center">
            <h2 className="mb-2 font-heading text-4xl font-semibold text-[#37699E]">
              {t('landing.workflow.title')}
            </h2>

            <p className="text-sm text-[#5A6B7A]">
              {t('landing.workflow.subtitle')}
            </p>
          </div>

          <div className="max-w-[709px] workflow:max-h-[1646px] mx-auto relative">
            {/* Desktop Layout (≥709px) */}
            <div className="hidden workflow:flex workflow:flex-col workflow:justify-between workflow:h-[1646px] workflow:py-[23px] relative" style={{
            backgroundImage: `url(${vectorBgGood})`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundSize: '100% 100%'
          }}>
              {/* Step 1 - Left side with white card */}
              <div ref={step1.ref} className={`flex items-start gap-8 pl-8 workflow:h-[203px] workflow:items-center opacity-100 translate-y-0 workflow:transition-all workflow:duration-700 ${step1.isVisible ? 'workflow:opacity-100 workflow:translate-y-0' : 'workflow:opacity-0 workflow:translate-y-10'}`}>
                <div className="flex-shrink-0 w-32">
                  <img src={step1Icon} alt="Step 1" className="w-24 h-24 animate-float" />
                </div>
                <div className="flex-1 bg-white rounded-2xl shadow-lg p-6 relative">
                  <span className="inline-block px-4 py-1 rounded-full bg-[#37699E] text-white text-xs font-medium mb-3">
                    {t('landing.workflow.steps.step1.badge')}
                  </span>
                  <h3 className="text-xl font-semibold text-[#2D3748] mb-2">
                    {t('landing.workflow.steps.step1.title')}
                  </h3>
                  <p className="text-sm text-[#5A6B7A] leading-relaxed">
                    {t('landing.workflow.steps.step1.description')}
                  </p>
                </div>
              </div>

              {/* Step 2 - Right side with blue card */}
              <div ref={step2.ref} className={`flex items-start gap-8 justify-end pr-[85px] workflow:h-[203px] workflow:items-center opacity-100 translate-y-0 workflow:transition-all workflow:duration-700 workflow:delay-100 ${step2.isVisible ? 'workflow:opacity-100 workflow:translate-y-0' : 'workflow:opacity-0 workflow:translate-y-10'}`}>
                <div className="flex-1 bg-[#37699E] rounded-2xl shadow-lg p-8 relative overflow-hidden text-right" style={{
                backgroundImage: `url(${backgroundDashedStep2})`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'left center',
                backgroundSize: 'auto 100%'
              }}>
                  <div className="absolute inset-0 bg-gradient-to-br from-[#2E5782]/20 to-transparent"></div>
                  <div className="absolute top-0 right-0 w-64 h-64 bg-[#2E5782]/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                  <div className="relative z-10">
                    <span className="inline-block px-4 py-1 rounded-full bg-white text-[#37699E] text-xs font-medium mb-3">
                      {t('landing.workflow.steps.step2.badge')}
                    </span>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {t('landing.workflow.steps.step2.title')}
                    </h3>
                    <p className="text-sm text-white/90 leading-relaxed">
                      {t('landing.workflow.steps.step2.description')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 3 - Left side with white card */}
              <div ref={step3.ref} className={`flex items-start gap-8 pl-8 workflow:h-[203px] workflow:items-center opacity-100 translate-y-0 workflow:transition-all workflow:duration-700 workflow:delay-200 ${step3.isVisible ? 'workflow:opacity-100 workflow:translate-y-0' : 'workflow:opacity-0 workflow:translate-y-10'}`}>
                <div className="flex-shrink-0 w-32">
                  <img src={step3Icon} alt="Step 3" className="w-24 h-24 animate-float" style={{
                  animationDelay: '0.5s'
                }} />
                </div>
                <div className="flex-1 bg-white rounded-2xl shadow-lg p-6 relative">
                  <span className="inline-block px-4 py-1 rounded-full bg-[#37699E] text-white text-xs font-medium mb-3">
                    {t('landing.workflow.steps.step3.badge')}
                  </span>
                  <h3 className="text-xl font-semibold text-[#2D3748] mb-2">
                    {t('landing.workflow.steps.step3.title')}
                  </h3>
                  <p className="text-sm text-[#5A6B7A] leading-relaxed">
                    {t('landing.workflow.steps.step3.description')}
                  </p>
                </div>
              </div>

              {/* Step 4 - Right side with blue card */}
              <div ref={step4.ref} className={`flex items-start gap-8 justify-end pr-[85px] workflow:h-[203px] workflow:items-center opacity-100 translate-y-0 workflow:transition-all workflow:duration-700 workflow:delay-300 ${step4.isVisible ? 'workflow:opacity-100 workflow:translate-y-0' : 'workflow:opacity-0 workflow:translate-y-10'}`}>
                <div className="flex-1 bg-[#37699E] rounded-2xl shadow-lg p-8 relative overflow-hidden text-right" style={{
                backgroundImage: `url(${backgroundDashedStep4})`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'left top',
                backgroundSize: 'auto 80%'
              }}>
                  <div className="absolute inset-0 bg-gradient-to-br from-[#2E5782]/20 to-transparent"></div>
                  <div className="absolute top-0 right-0 w-64 h-64 bg-[#2E5782]/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                  <div className="relative z-10">
                    <span className="inline-block px-4 py-1 rounded-full bg-white text-[#37699E] text-xs font-medium mb-3">
                      {t('landing.workflow.steps.step4.badge')}
                    </span>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {t('landing.workflow.steps.step4.title')}
                    </h3>
                    <p className="text-sm text-white/90 leading-relaxed">
                      {t('landing.workflow.steps.step4.description')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 5 - Left side with white card */}
              <div ref={step5.ref} className={`flex items-start gap-8 pl-8 workflow:h-[203px] workflow:items-center opacity-100 translate-y-0 workflow:transition-all workflow:duration-700 workflow:delay-[400ms] ${step5.isVisible ? 'workflow:opacity-100 workflow:translate-y-0' : 'workflow:opacity-0 workflow:translate-y-10'}`}>
                <div className="flex-shrink-0 w-32">
                  <img src={step5Icon} alt="Step 5" className="w-24 h-24 animate-float" style={{
                  animationDelay: '1s'
                }} />
                </div>
                <div className="flex-1 bg-white rounded-2xl shadow-lg p-6 relative">
                  <span className="inline-block px-4 py-1 rounded-full bg-[#37699E] text-white text-xs font-medium mb-3">
                    {t('landing.workflow.steps.step5.badge')}
                  </span>
                  <h3 className="text-xl font-semibold text-[#2D3748] mb-2">
                    {t('landing.workflow.steps.step5.title')}
                  </h3>
                  <p className="text-sm text-[#5A6B7A] leading-relaxed">
                    {t('landing.workflow.steps.step5.description')}
                  </p>
                </div>
              </div>

              {/* Step 6 - Centered text only */}
              <div ref={step6.ref} className={`text-center workflow:h-[203px] workflow:flex workflow:items-center workflow:justify-center opacity-100 translate-y-0 workflow:transition-all workflow:duration-700 workflow:delay-500 ${step6.isVisible ? 'workflow:opacity-100 workflow:translate-y-0' : 'workflow:opacity-0 workflow:translate-y-10'}`}>
                <div className="relative w-full bg-[#FFBF07] rounded-3xl shadow-lg p-8 overflow-hidden" style={{
                backgroundImage: `url(${backgroundDashedStep6})`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'left center',
                backgroundSize: 'auto 100%'
              }}>
                  <div className="flex flex-col items-end justify-center space-y-3 text-right">
                    <span className="inline-block px-6 py-2 rounded-full bg-[#37699E] text-white text-sm font-medium">
                      {t('landing.workflow.steps.step6.badge')}
                    </span>
                    <h3 className="text-2xl font-semibold text-[#37699E]">
                      {t('landing.workflow.steps.step6.title')}
                    </h3>
                    <p className="text-base text-[#37699E] leading-relaxed max-w-xl">
                      {t('landing.workflow.steps.step6.description')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Layout (<709px) */}
            <div className="workflow:hidden space-y-8">
              {/* Step 1 */}
              <div className="flex flex-col items-center opacity-100 translate-y-0">
                <img src={step1Icon} alt="Step 1" className="w-16 h-16 mb-4 animate-float" />
                <div className="bg-white rounded-2xl shadow-lg p-6 w-full text-center">
                  <span className="inline-block px-4 py-1 rounded-full bg-[#37699E] text-white text-xs font-medium mb-3">
                    {t('landing.workflow.steps.step1.badge')}
                  </span>
                  <h3 className="text-lg font-semibold text-[#2D3748] mb-2">
                    {t('landing.workflow.steps.step1.title')}
                  </h3>
                  <p className="text-sm text-[#5A6B7A] leading-relaxed">
                    {t('landing.workflow.steps.step1.description')}
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex flex-col items-center opacity-100 translate-y-0">
                <TruckIcon className="w-16 h-16 text-white mb-4" strokeWidth={1.5} />
                <div className="bg-[#37699E] rounded-2xl shadow-lg p-6 w-full text-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#2E5782]/20 to-transparent"></div>
                  <div className="relative z-10">
                    <span className="inline-block px-4 py-1 rounded-full bg-white text-[#37699E] text-xs font-medium mb-3">
                      {t('landing.workflow.steps.step2.badge')}
                    </span>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {t('landing.workflow.steps.step2.title')}
                    </h3>
                    <p className="text-sm text-white/90 leading-relaxed">
                      {t('landing.workflow.steps.step2.description')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex flex-col items-center opacity-100 translate-y-0">
                <img src={step3Icon} alt="Step 3" className="w-16 h-16 mb-4 animate-float" style={{
                animationDelay: '0.5s'
              }} />
                <div className="bg-white rounded-2xl shadow-lg p-6 w-full text-center">
                  <span className="inline-block px-4 py-1 rounded-full bg-[#37699E] text-white text-xs font-medium mb-3">
                    {t('landing.workflow.steps.step3.badge')}
                  </span>
                  <h3 className="text-lg font-semibold text-[#2D3748] mb-2">
                    {t('landing.workflow.steps.step3.title')}
                  </h3>
                  <p className="text-sm text-[#5A6B7A] leading-relaxed">
                    {t('landing.workflow.steps.step3.description')}
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex flex-col items-center opacity-100 translate-y-0">
                <div className="bg-[#37699E] rounded-2xl shadow-lg p-6 w-full text-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#2E5782]/20 to-transparent"></div>
                  <div className="relative z-10">
                    <span className="inline-block px-4 py-1 rounded-full bg-white text-[#37699E] text-xs font-medium mb-3">
                      {t('landing.workflow.steps.step4.badge')}
                    </span>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {t('landing.workflow.steps.step4.title')}
                    </h3>
                    <p className="text-sm text-white/90 leading-relaxed">
                      {t('landing.workflow.steps.step4.description')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 5 */}
              <div className="flex flex-col items-center opacity-100 translate-y-0">
                <img src={step5Icon} alt="Step 5" className="w-16 h-16 mb-4 animate-float" style={{
                animationDelay: '1s'
              }} />
                <div className="bg-white rounded-2xl shadow-lg p-6 w-full text-center">
                  <span className="inline-block px-4 py-1 rounded-full bg-[#37699E] text-white text-xs font-medium mb-3">
                    {t('landing.workflow.steps.step5.badge')}
                  </span>
                  <h3 className="text-lg font-semibold text-[#2D3748] mb-2">
                    {t('landing.workflow.steps.step5.title')}
                  </h3>
                  <p className="text-sm text-[#5A6B7A] leading-relaxed">
                    {t('landing.workflow.steps.step5.description')}
                  </p>
                </div>
              </div>

              {/* Step 6 */}
              <div className="flex flex-col items-center opacity-100 translate-y-0">
                <div className="bg-[#FFBF07] rounded-2xl shadow-lg p-6 w-full text-center">
                  <h3 className="text-xl font-semibold text-[#2D3748] mb-2">
                    {t('landing.workflow.steps.step6.title')}
                  </h3>
                  <p className="text-sm text-[#2D3748] leading-relaxed">
                    {t('landing.workflow.steps.step6.description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-16">
              <h2 className="font-semibold font-['Lexend_Deca'] mb-4 text-4xl text-[#37699e]">
                {t('landing.faq.title')}
              </h2>
              <p className="text-lg text-[#5A6B7A] font-['Funnel_Sans'] max-w-2xl mx-auto">
                {t('landing.faq.subtitle')}
              </p>
            </div>

            {/* FAQ Items */}
            <Accordion type="single" collapsible className="space-y-3">
              <AccordionItem value="whatIsVelox" className="bg-white rounded-xl border border-[#DBE2EA] px-6 transition-all hover:border-[#0880AE] hover:shadow-md">
                <AccordionTrigger className="text-left font-semibold text-[#2D3748] hover:text-[#0880AE] font-['Funnel_Sans'] py-4 hover:no-underline">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#EBF4F8] flex items-center justify-center flex-shrink-0">
                      <Truck className="text-[#0880AE]" size={20} />
                    </div>
                    <span>{t('landing.faq.questions.whatIsVelox.question')}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-[#5A6B7A] leading-relaxed font-['Funnel_Sans'] pl-12 pr-4 pb-4">
                  {t('landing.faq.questions.whatIsVelox.answer')}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="needDownload" className="bg-white rounded-xl border border-[#DBE2EA] px-6 transition-all hover:border-[#0880AE] hover:shadow-md">
                <AccordionTrigger className="text-left font-semibold text-[#2D3748] hover:text-[#0880AE] font-['Funnel_Sans'] py-4 hover:no-underline">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#EBF4F8] flex items-center justify-center flex-shrink-0">
                      <Smartphone className="text-[#0880AE]" size={20} />
                    </div>
                    <span>{t('landing.faq.questions.needDownload.question')}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-[#5A6B7A] leading-relaxed font-['Funnel_Sans'] pl-12 pr-4 pb-4">
                  {t('landing.faq.questions.needDownload.answer')}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="whoCanUse" className="bg-white rounded-xl border border-[#DBE2EA] px-6 transition-all hover:border-[#0880AE] hover:shadow-md">
                <AccordionTrigger className="text-left font-semibold text-[#2D3748] hover:text-[#0880AE] font-['Funnel_Sans'] py-4 hover:no-underline">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#EBF4F8] flex items-center justify-center flex-shrink-0">
                      <Users className="text-[#0880AE]" size={20} />
                    </div>
                    <span>{t('landing.faq.questions.whoCanUse.question')}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-[#5A6B7A] leading-relaxed font-['Funnel_Sans'] pl-12 pr-4 pb-4">
                  {t('landing.faq.questions.whoCanUse.answer')}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="driverJoin" className="bg-white rounded-xl border border-[#DBE2EA] px-6 transition-all hover:border-[#0880AE] hover:shadow-md">
                <AccordionTrigger className="text-left font-semibold text-[#2D3748] hover:text-[#0880AE] font-['Funnel_Sans'] py-4 hover:no-underline">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#EBF4F8] flex items-center justify-center flex-shrink-0">
                      <UserCheck className="text-[#0880AE]" size={20} />
                    </div>
                    <span>{t('landing.faq.questions.driverJoin.question')}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-[#5A6B7A] leading-relaxed font-['Funnel_Sans'] pl-12 pr-4 pb-4">
                  {t('landing.faq.questions.driverJoin.answer')}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="deliveryValidation" className="bg-white rounded-xl border border-[#DBE2EA] px-6 transition-all hover:border-[#0880AE] hover:shadow-md">
                <AccordionTrigger className="text-left font-semibold text-[#2D3748] hover:text-[#0880AE] font-['Funnel_Sans'] py-4 hover:no-underline">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#EBF4F8] flex items-center justify-center flex-shrink-0">
                      <QrCode className="text-[#0880AE]" size={20} />
                    </div>
                    <span>{t('landing.faq.questions.deliveryValidation.question')}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-[#5A6B7A] leading-relaxed font-['Funnel_Sans'] pl-12 pr-4 pb-4">
                  {t('landing.faq.questions.deliveryValidation.answer')}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="notifications" className="bg-white rounded-xl border border-[#DBE2EA] px-6 transition-all hover:border-[#0880AE] hover:shadow-md">
                <AccordionTrigger className="text-left font-semibold text-[#2D3748] hover:text-[#0880AE] font-['Funnel_Sans'] py-4 hover:no-underline">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#EBF4F8] flex items-center justify-center flex-shrink-0">
                      <Bell className="text-[#0880AE]" size={20} />
                    </div>
                    <span>{t('landing.faq.questions.notifications.question')}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-[#5A6B7A] leading-relaxed font-['Funnel_Sans'] pl-12 pr-4 pb-4">
                  {t('landing.faq.questions.notifications.answer')}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="multilingual" className="bg-white rounded-xl border border-[#DBE2EA] px-6 transition-all hover:border-[#0880AE] hover:shadow-md">
                <AccordionTrigger className="text-left font-semibold text-[#2D3748] hover:text-[#0880AE] font-['Funnel_Sans'] py-4 hover:no-underline">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#EBF4F8] flex items-center justify-center flex-shrink-0">
                      <Globe className="text-[#0880AE]" size={20} />
                    </div>
                    <span>{t('landing.faq.questions.multilingual.question')}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-[#5A6B7A] leading-relaxed font-['Funnel_Sans'] pl-12 pr-4 pb-4">
                  {t('landing.faq.questions.multilingual.answer')}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="payment" className="bg-white rounded-xl border border-[#DBE2EA] px-6 transition-all hover:border-[#0880AE] hover:shadow-md">
                <AccordionTrigger className="text-left font-semibold text-[#2D3748] hover:text-[#0880AE] font-['Funnel_Sans'] py-4 hover:no-underline">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#EBF4F8] flex items-center justify-center flex-shrink-0">
                      <CreditCard className="text-[#0880AE]" size={20} />
                    </div>
                    <span>{t('landing.faq.questions.payment.question')}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-[#5A6B7A] leading-relaxed font-['Funnel_Sans'] pl-12 pr-4 pb-4">
                  {t('landing.faq.questions.payment.answer')}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="history" className="bg-white rounded-xl border border-[#DBE2EA] px-6 transition-all hover:border-[#0880AE] hover:shadow-md">
                <AccordionTrigger className="text-left font-semibold text-[#2D3748] hover:text-[#0880AE] font-['Funnel_Sans'] py-4 hover:no-underline">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#EBF4F8] flex items-center justify-center flex-shrink-0">
                      <History className="text-[#0880AE]" size={20} />
                    </div>
                    <span>{t('landing.faq.questions.history.question')}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-[#5A6B7A] leading-relaxed font-['Funnel_Sans'] pl-12 pr-4 pb-4">
                  {t('landing.faq.questions.history.answer')}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="production" className="bg-white rounded-xl border border-[#DBE2EA] px-6 transition-all hover:border-[#0880AE] hover:shadow-md">
                <AccordionTrigger className="text-left font-semibold text-[#2D3748] hover:text-[#0880AE] font-['Funnel_Sans'] py-4 hover:no-underline">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#EBF4F8] flex items-center justify-center flex-shrink-0">
                      <Zap className="text-[#0880AE]" size={20} />
                    </div>
                    <span>{t('landing.faq.questions.production.question')}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-[#5A6B7A] leading-relaxed font-['Funnel_Sans'] pl-12 pr-4 pb-4">
                  {t('landing.faq.questions.production.answer')}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="border-t border-border bg-gradient-to-br from-secondary to-secondary-hover py-20 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="mb-6 font-heading text-4xl font-semibold">
            {t('landing.ctaSection.title')}
          </h2>
          <p className="mb-8 text-lg text-white/90">
            {t('landing.ctaSection.subtitle')}
          </p>
          <Link to="/auth?tab=signup">
            <Button size="lg" className="bg-primary hover:bg-primary-hover text-primary-foreground font-semibold px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-200">
              {t('landing.ctaSection.createAccount')}
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <AppFooter />
    </div>;
};
export default Landing;